<template>
  <div class="question-container">
    <!-- PREVIEW MODE -->
    <div v-if="mode === 0">
      <div class="row">
        <div class="col-md-5 d-flex pl-0">
          <span
            v-if="surveyData.showNumericLabels || surveyData.showNumericLabels == undefined || surveyData.showNumericLabels == null"
            class="index-no">{{ stepNo }}.</span>
          <template v-if="activityType == 'activity'">
            <span class="question-text" v-html="question.formattedBody"></span>
          </template>
          <template v-else>
            <span v-if="selectedLanguageCulture == 'EN'" class="question-text"
                  v-html="newAnswer[usage.QUESTION_TEXT]"></span>
            <span v-else class="question-text" v-html="newAnswer[usage.DISPLAY_QUESTION_TEXT]"></span>
          </template>
          <span v-if="question.isSkippable"
                class="activity-form-preview-components__optional-box ml-15 d-inline-box pt-2">Optional</span>
        </div>

        <div class="col-md-7" data-test="answer-preview">
          <template v-if="newAnswer && newAnswer.answerText">
            <div>{{ legacyAnswerWithoutOtherComment }}</div>

            <div v-if="newOtherComment" class="other-answer-display">
              <div class="other-answer-display-title">{{ question.otherOptionContentField || "Other" }}:</div>
              <div class="other-answer-display-text">{{ newOtherComment }}</div>
            </div>
          </template>
        </div>

        <template v-if="newAnswer && newAnswer.answerText">
          <div v-if="showScore && activityType == 'activity' && newAnswer.score != null"
               class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ newScore }}
            </div>
          </div>
        </template>
        <template v-else-if="question.isSkippable">
          <div v-if="showScore && activityType == 'activity'" class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ question.skipScore || 0 }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- EDIT MODE -->
    <div v-if="mode === 1">
      <div class="row">
        <div class="col-md-5 d-flex pl-0">
          <span
            v-if="surveyData.showNumericLabels || surveyData.showNumericLabels == undefined || surveyData.showNumericLabels == null"
            :class="newAnswer.isSkipped ? 'disable-question' : ''"
            class="index-no">{{ stepNo }}.</span>
          <template v-if="activityType == 'activity'">
            <span :class="newAnswer.isSkipped ? 'disable-question' : ''" class="question-text"
                  v-html="question.formattedBody"></span>
          </template>
          <template v-else>
            <span v-if="selectedLanguageCulture == 'EN'" :class="newAnswer.isSkipped ? 'disable-question' : ''"
                  class="question-text" v-html="newAnswer[usage.QUESTION_TEXT]"></span>
            <span v-else :class="newAnswer.isSkipped ? 'disable-question' : ''" class="question-text"
                  v-html="newAnswer[usage.DISPLAY_QUESTION_TEXT]"></span>
          </template>
          <span v-if="question.isSkippable"
                :class="newAnswer.isSkipped ? 'disable-question' : ''"
                class="activity-form-preview-components__optional-box ml-15 d-inline-box pt-2">Optional</span>
          <span v-if="newAnswer.isSkipped" class="skipped-question-box ml-15 d-inline-box pt-2">Skipped</span>
        </div>
        <div :class="newAnswer.isSkipped ? 'disable-question' : ''" class="col-md-7" data-test="answer-edit">
          <div>
            <b-form-group>
              <component :is="isMultiChoice ? 'b-form-checkbox-group' : 'b-form-radio-group'" :checked="question.subType === 'Multi' ? newOptionIds : newOptionIds[0]" :disabled="isAnswerDisabled"
                                  :name="`multiChoice${question.id}`" stacked data-test="form-input-group"
                                  @change="onAnswerChange($event)">
                <component :is="isMultiChoice ? 'b-form-checkbox' : 'b-form-radio'" v-for="option in question.options" :key="option.id" :value="option.id" :id="'question_' + question.id + '_multichoiceInput_' + option.id">
                  {{ option.body }}
                </component>
                <component :is="isMultiChoice ? 'b-form-checkbox' : 'b-form-radio'" v-if="isOtherOptionEnabled" :value="question.otherOptionId" :id="'question_' + question.id + '_multichoiceInput_' + question.otherOptionId">
                  {{ question.otherOptionContentField }}
                </component>
                <div v-if="isOtherOptionCommentEnabled && isOtherSelected"
                      :class="{ 'disabled-area': !isOtherSelected, 'active-area': isOtherSelected }"
                      class="other-option-comment">
                  <textarea :value="newOtherComment"
                            :id="'textarea' + question.otherOptionId"
                            @input="onInputOtherComment"
                            @keydown="onKeydownOtherComment"
                            :disabled="!isOtherSelected"
                            :maxlength="commentMaxLength"
                            :placeholder="question.otherCommentSelectedField"
                            class="other-option-comment-area">
                  </textarea>
                  <div :class="{'max-character-limit': otherCommentLength >= commentMaxLength}"
                        class="other-option-comment-count">
                    {{ otherCommentLength }} / {{ commentMaxLength }}
                  </div>
                </div>
              </component>
              <span data-test="fieldWarningText" v-if="fieldWarningText" class="text-warning pl-15 fw-bold">{{ fieldWarningText }} </span>
              <span data-test="fieldErrorText" v-if="fieldErrorText" class="text-danger fw-bold">{{ fieldErrorText }} </span>
            </b-form-group>
          </div>
        </div>

        <template v-if="newAnswer && newAnswer.answerText">
          <div v-if="showScore && activityType == 'activity' && newAnswer.score != null"
               class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ newScore }}
            </div>
          </div>
        </template>
        <template v-else-if="question.isSkippable">
          <div v-if="showScore && activityType == 'activity'" :class="newAnswer.isSkipped ? 'disable-question' : ''"
               class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ question.skipScore || 0 }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- RESPONSE MODE -->
    <div v-else-if="mode == 2">
      <div class="row">
        <div class="col-md-5 d-flex pl-0">
          <div class="row">
            <div class="col-md-12 d-flex p-0 answers">
              <span v-if="newAnswer && newAnswer.edit_status == 1" class="changed"></span>
              <span
                v-if="surveyData.showNumericLabels || surveyData.showNumericLabels == undefined || surveyData.showNumericLabels == null"
                class="index-no">{{ stepNo }}.</span>
              <template v-if="activityType == 'activity'">
                <span class="question-text" v-html="question.formattedBody"></span>
              </template>
              <template v-else>
                <span v-if="selectedLanguageCulture == 'EN'" class="question-text"
                      v-html="newAnswer[usage.QUESTION_TEXT]"></span>
                <span v-else class="question-text" v-html="newAnswer[usage.DISPLAY_QUESTION_TEXT]"></span>
              </template>
              <span v-if="question.isSkippable"
                    class="activity-form-preview-components__optional-box ml-15 d-inline-box pt-2">Optional</span>
            </div>
            <!-- CURRENT ANSWER -->
            <template v-if="newAnswer && newAnswer.answer_text">
              <div class="col-md-12 d-flex pl-30">
                <div class="wrap" data-test="answer-response">
                  <div>{{ currentAnswerWithoutOther }}</div>

                  <div v-if="currentOtherAnswer" class="other-answer-display">
                    <div class="other-answer-display-title">{{ question.otherOptionContentField || "Other" }}:</div>
                    <div class="other-answer-display-text">{{ currentOtherAnswer }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div v-if="showScore && activityType == 'activity' && newAnswer.score != null"
                     class="d-inline-block ml-25 pt-10">
                  <label>Score</label>
                  <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                    {{ newScore }}
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="question.isSkippable">
              <div v-if="showScore && activityType == 'activity'" class="ml-25 d-inline-block pt-20">
                <label class="fs-12">Score</label>
                <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                  {{ question.skipScore || 0 }}
                </div>
              </div>
            </template>
          </div>
        </div>
        <div v-if="newAnswer && newAnswer.edit_status == 1" class="col-md-7 previous-answer">
          <div>
            <span class="previous-value-mark"></span>
            <div class="previous-value ml-3">PREVIOUS RESPONSE</div>

            <template v-if="newAnswer && newAnswer.privius_answer_text">
              <div class="wrap">
                <div>{{ previousAnswerWithoutOther }}</div>

                <div v-if="previousOtherAnswer" class="other-answer-display">
                  <div class="other-answer-display-title">{{ question.otherOptionContentField || "Other" }}:</div>
                  <div class="other-answer-display-text">{{ previousOtherAnswer }}</div>
                </div>
              </div>

              <div class="row">
                <div v-if="showScore && newAnswer.privius_score != null" class="d-inline-block ml-0 pt-10">
                  <label>Score</label>
                  <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                    {{ newAnswer.privius_score }}
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="question.isSkippable">
              <div v-if="showScore && activityType == 'activity'" class="ml-25 d-inline-block pt-20">
                <label class="fs-12">Score</label>
                <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                  {{ question.skipScore || 0 }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import activityCommonMixin from "./activity-common-mixin";
import _ from "lodash";
import { splitOnN } from "@/utils/stringUtils";
import * as MultiChoiceService from "@/components/common/multi-choice/multi-choice-service";

export default {
  name: "MultiChoiceResponse",
  mixins: [activityCommonMixin],
  data() {
    return {
      newOptionIds: [],
      newOtherComment: "",
      newAnswer: {},
      fieldErrorText: "",
      fieldWarningText: "",
      scoresMap: {},
      noneOptionId: "",
      commentMaxLength: 1000,
    };
  },
  props: {
    mode: Number,
    answer: Object,
    question: Object,
    surveyData: Object,
    usage: Object,
    stepIndex: Number,
    selectedLanguageCulture: String,
    activityType: String
  },
  mounted() {
    this.initNewAnswer();
    this.feedScoresMap();
    this.updateAnswersResponse();
  },
  computed: {
    isMultiChoice() {
      return this.question.subType === 'Multi' ? true : false;
    },
    isOtherSelected() {
      return this.newOptionIds.includes(this.question.otherOptionId);
    },
    isAnswerDisabled() {
      return this.newAnswer ? this.newAnswer.isSkipped === true : false;
    },
    otherCommentLength() {
      return this.newOtherComment ? this.newOtherComment.length : 0;
    },
    newScore() {
      if (this.newOptionIds.length && Object.keys(this.scoresMap).length) {
        return this.newOptionIds.reduce((acc, id) => acc += this.scoresMap[id] ? this.scoresMap[id] : 0, 0);
      }
      return 0;
    },
    legacyAnswerWithoutOtherComment() {
      const answerWithoutOther = this.question && this.question.options ? this.question.options.filter(o => this.newOptionIds.includes(o.id)).map(o => o.body).join(", ") : "";

      if (!this.isOtherOptionEnabled) {
        return answerWithoutOther;
      }

      const otherLabel = this.isOtherSelected ? this.question.otherOptionContentField || "Other" : "";
      return this.isOtherOptionCommentEnabled ? answerWithoutOther : [answerWithoutOther, otherLabel].filter(a => a !== "").join(", ");
    },
    currentAnswerWithoutOther() {
      const answer = this.selectedLanguageCulture === "EN" ? this.newAnswer.answer_text : this.newAnswer.display_answer_text;
      return this.getDisplayAnswerWithoutOther(answer, this.newAnswer.option_id);
    },
    currentOtherAnswer() {
      const answer = this.selectedLanguageCulture === "EN" ? this.newAnswer.answer_text : this.newAnswer.display_answer_text;
      return this.getDisplayOtherAnswer(answer, this.newAnswer.option_id);
    },
    previousAnswerWithoutOther() {
      const answer = this.selectedLanguageCulture === "EN" ? this.newAnswer.privius_answer_text : this.newAnswer.privius_display_answer_text;
      return this.getDisplayAnswerWithoutOther(answer, this.newAnswer.privius_optionId);
    },
    previousOtherAnswer() {
      const answer = this.selectedLanguageCulture === "EN" ? this.newAnswer.privius_answer_text : this.newAnswer.privius_display_answer_text;
      return this.getDisplayOtherAnswer(answer, this.newAnswer.privius_optionId);
    },
    isOtherOptionCommentEnabled() {
      return MultiChoiceService.isOtherOptionCommentEnabled(this.question);
    },
    isOtherOptionEnabled() {
      return MultiChoiceService.isOtherOptionEnabled(this.question);
    },
  },
  methods: {
    initNewAnswer(answer = this.answer) {
      if (answer) {
        this.newAnswer = _.cloneDeep(answer);

        // ensure that Other option id is in the last optionId
        if (this.question.otherOptionId && this.newAnswer.optionId) {
          const optionsIds = this.newAnswer.optionId.split(',') || [];
          optionsIds.push(optionsIds.splice(optionsIds.indexOf(this.question.otherOptionId), 1)[0]);
          const sortedOptionsIds = optionsIds.join(',');
          this.newAnswer.optionId = sortedOptionsIds;
        }

        if (this.mode === 2 && this.newAnswer.option_id) {
          this.newOptionIds = this.newAnswer.option_id.split(",");
        } else if (this.newAnswer.optionId) {
          this.newOptionIds = this.newAnswer.optionId.split(",");
          this.newOtherComment = this.getOtherCommentFromAnswer();
        }
      }
    },
    // this method is called when edition is canceled from its parent
    onEditComplete(answer) {
      // if the edit form is cancelled, answer is null. When is saved, it has the latest answer
      this.initNewAnswer(answer);
    },
    getOtherCommentFromAnswer() {
      const displayAnswerText = this.newAnswer.displayAnswerText;
      if (!this.question || !this.question.options) {
        return null;
      }

      let otherCommentResponse = "";

      if (!this.question.otherOptionId || !this.isOtherOptionCommentEnabled || !displayAnswerText) {
        return otherCommentResponse;
      }

      const otherOptionIdx = this.newOptionIds.indexOf(this.question.otherOptionId);
      const answeredOptions = this.newAnswer.optionId.split(",");

      let answers = [];

      if (displayAnswerText instanceof Array && displayAnswerText.length) {
        // multiple answers could be grouped into the first array element
        answers = displayAnswerText.length === answeredOptions.length
          ? displayAnswerText
          : splitOnN(displayAnswerText[0], ",", answeredOptions.length - 1);
      } else if (typeof displayAnswerText === "string" && displayAnswerText) {
        answers = splitOnN(displayAnswerText, ",", answeredOptions.length - 1);
      }

      if (otherOptionIdx !== -1 && otherOptionIdx === answers.length - 1) {
        const otherSplit = answers[otherOptionIdx].split(":");

        if (otherSplit.length > 1) {
          otherCommentResponse = otherSplit[otherSplit.length - 1].trim();
        }
      }

      return otherCommentResponse;
    },
    getOptionsIdAndAnswers(answer, optionId) {
      if (!answer) {
        return {};
      }

      const optionsIds = optionId.split(",");

      return {
        optionsIds,
        answers: splitOnN(answer, ",", optionsIds.length - 1),
      }
    },
    getDisplayAnswerWithoutOther(answer, optionId) {
      const { optionsIds, answers } = this.getOptionsIdAndAnswers(answer, optionId);

      if (!this.isOtherOptionCommentEnabled) {
        return answers.join(", ");
      }

      const otherIdx = optionsIds.indexOf(this.question.otherOptionId);
      return answers.filter((a, idx) => otherIdx === -1 ? true : idx !== otherIdx).join(", ");
    },
    getDisplayOtherAnswer(answer, optionId) {
      const { optionsIds, answers } = this.getOptionsIdAndAnswers(answer, optionId);

      let otherAnswer = null;
      if (this.isOtherOptionCommentEnabled) {
        const index = optionsIds.indexOf(this.question.otherOptionId);
        const otherAnswerWithLabel = (answers[index] || "").split(":");
        otherAnswer = otherAnswerWithLabel[1] || otherAnswerWithLabel[0];
      }

      return otherAnswer ? otherAnswer.trim() : "";
    },
    updateAnswersResponse() {
      this.checkForFieldErrors();

      if (this.fieldErrorText) {
        this.resetFields();
        return;
      }

      if (this.question.options) {
        const selectedOptions = this.newOptionIds;
        const hasOtherOption = selectedOptions.includes(this.question.otherOptionId);
        const isOtherOptionRequired = hasOtherOption && this.isOtherOptionEnabled;

        // Important: always sort options ids by sequence and other option id as the last element
        const sortedOptions = this.question.options.sort((a, b) => a.sequence - b.sequence).filter(opt => selectedOptions.includes(opt.id));

        // Other answer must be concatenated at the end
        let otherAnswerBody = ""
        if (isOtherOptionRequired) {
          if (this.isOtherOptionCommentEnabled && this.newOtherComment) {
            otherAnswerBody = `${this.question.otherOptionContentField || "Other"}: ${this.newOtherComment}`;
          } else {
            otherAnswerBody = `${this.question.otherOptionContentField || "Other"}`;
          }
        }

        // Important: always sort options ids by sequence and other option id as the last element
        this.newAnswer.optionId = sortedOptions.map(o => o.id).concat(isOtherOptionRequired ? [this.question.otherOptionId] : []).join(",");
        this.newAnswer.answerText = sortedOptions.map(o => (o ? o.body : null)).filter(b => b !== null).concat(otherAnswerBody ? [otherAnswerBody] : []).join(", ");
        // TODO: investigate if this is needed in the parent and try to remove it
        this.newAnswer.score = this.newScore;

        this.$emit(`answerChanged`, {
          questionId: this.question.id,
          newAns: {
            ...this.newAnswer,
            _otherOptionComment: this.newOtherComment,
          }
        });
      }
    },
    resetFields() {
      this.newAnswer = {
        ...this.newAnswer,
        optionId: "",
        answerText: "",
        score: 0,
        computedScore: 0,
      }
    },
    onAnswerChange(value) {
      // none option functionality is commented out as it should be treated in another story
      // const optionIds = this.isMultiChoice ? value : [value];
      // let noneOption = _.find(this.question.options, o => (o.formattedBody || "").toLowerCase() === "none");
      // if (noneOption) {
      //   this.noneOptionId = noneOption.id;
      // }
      // if (optionIds && optionIds.includes(this.noneOptionId)) {
      //   this.newOptionIds = [];
      //   this.newOptionIds.push(this.noneOptionId);
      // } else {
      //   this.newOptionIds = optionIds;
      // }
      this.newOptionIds = this.isMultiChoice ? value : [value];
      this.newAnswer.optionsIds = this.newOptionIds;
      this.updateAnswersResponse();
    },
    feedScoresMap() {
      if (this.question.options) {
        this.question.options.forEach((option) => {
          this.scoresMap[option.id] = option.score || 0;
        });
      }
      if (this.question.otherOptionId && this.question.otherScore) {
        this.scoresMap[this.question.otherOptionId] = this.question.otherScore || 0;
      }
    },
    validateAll(alsoSetWarning = false) {
      // don't validate answer if question was skipped
      if (this.newAnswer && this.newAnswer.isSkipped) {
        return;
      }

      let canBeSkipped = false;
      // don't validate answer if question is skippable
      if (this.question && this.question.isSkippable) {
        canBeSkipped = true;
      }

      if (!canBeSkipped && (!this.newOptionIds || !this.newOptionIds.length)) {
        this.setFieldErrorText("This field is required.");
        if (alsoSetWarning) {
          this.fieldWarningText = "This field is required.";
        }
      } else {
        if (this.isOtherOptionEnabled && this.isOtherSelected && this.isOtherOptionCommentEnabled && !this.newOtherComment) {
          this.setFieldErrorText("Please enter a comment.");
          if (alsoSetWarning) {
            this.fieldWarningText = "Please enter a comment.";
          }
        }
      }
    },
    checkForFieldErrors() {
      this.resetError();
      this.question.dataChecks = [];
      if (this.question.dataEditChecks && this.question.dataEditChecks.length) {
        for (let k = 0; k < this.question.dataEditChecks.length; k++) {
          const dataEditCheck = this.question.dataEditChecks[k];
          if (dataEditCheck.check === "SOFT") {
            if (dataEditCheck.type === "NOT_BLANK" && !this.newOptionIds.length) {
              this.validateAll(true);
              this.question.dataChecks.push({
                dataCheckId: dataEditCheck.id,
                dataCheckType: dataEditCheck.type,
                message: `This field is required.`
              });
            }
          } else {
            if (dataEditCheck.type === "NOT_BLANK" && !this.newOptionIds.length) {
              this.validateAll();
            }
          }
        }
      } else {
        this.validateAll();
      }
    },
    resetError() {
      this.setFieldErrorText("");
      this.fieldWarningText = "";
    },
    setFieldErrorText(val) {
      this.fieldErrorText = val;
      this.question.hasFieldError = !!val;
      this.question.fieldErrorText = val;
    },
    onInputOtherComment(event) {
      const newValue = event.target.value;
      this.newOtherComment = newValue.replace(/:/g, "");
      this.updateAnswersResponse();
    },
    onKeydownOtherComment(event) {
      if (event.key === ":") {
        event.preventDefault();
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/edit-site-partcipant-activity.scss";

.changed {
  position: absolute;
  left: -29px;
  top: 0;
  width: 18px;
  height: 51px;
  border-radius: 1px;
  background-color: #f5a623;
}

.question-text {
  display: inline;
}

.previous-value-mark {
  width: 9px;
  height: 51px;
  background-color: #f5a623;
  border-radius: 1px;
  left: 0;
  top: 4px;
  position: absolute;
}

.previous-value {
  /* font-family: Roboto; */
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  color: #f5a623;
  margin-bottom: 9px;
  margin-top: 3px;
}

.answers {
  font-weight: 600;
  color: #000000;
}

.other-option-comment {
  position: relative;
  border-radius: 5px;
  box-shadow: 0 0 0 1px #4a4a4a;
  padding-bottom: 20px;
}

.disabled-area {
  background-color: rgb(245, 245, 245);
}

.active-area {
  box-shadow: 0 0 0 1px #007bff;
}

.other-option-comment-area {
  resize: none;
  width: 100%;
  padding: 10px;
  border: 0;
}

.other-option-comment-count {
  position: absolute;
  bottom: 5px;
  right: 10px;
  font-size: small;
}

.max-character-limit {
  color: crimson;
}

.other-answer-display {
  margin-top: 15px;
}

.other-answer-display-title {
  font-weight: bold;
  color: black;
}

.other-answer-display-text {
  font-style: italic;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-all;
  word-break: break-word;
  hyphens: auto;
}
</style>
